import { Injectable } from '@angular/core';

import * as Twilio from 'twilio-client';

import { HttpHeaders } from '@angular/common/http';
import firebase from 'firebase/app';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
    ProviderEndpoints,
    Ticket,
    TokenResponse,
    VoiceConnection,
    VoiceConnectionStatus,
    VoiceDevice,
    VoiceDeviceStatus,
} from '../../../models';
import { ParentVoiceService } from '../parent-voice.service';

@Injectable({
    providedIn: 'root',
})
export class TwilioVoiceService extends ParentVoiceService {
    token: string;
    expiration: number;

    providerEndpoints: ProviderEndpoints = {
        TOKEN_URL: environment.twilio.TOKEN_URL,
        OUTBOUND_REQUEST_URL: environment.twilio.OUTBOUND_REQUEST_URL,
        CONFERENCE_HOLD_URL: environment.twilio.CONFERENCE_HOLD_URL,
        CALL_TRANSFER_URL: environment.twilio.CALL_TRANSFER_URL,
        CREATE_CALL_URL: environment.twilio.CREATE_CALL_URL,
    };

    setupDevice(idToken: string): Promise<VoiceDevice> {
        const device = new Twilio.Device();

        // Fired when the connection becomes ready to accept connections
        device.on('ready', () => {
            this.deviceStatusSubject.next({ status: VoiceDeviceStatus.Ready });
        });

        // Fired when the connection is lost, or when the token becomes expired/invalid
        device.on('offline', () => {
            this.deviceStatusSubject.next({ status: VoiceDeviceStatus.Offline });
            if (!!this.token && this.deviceTokenExpired()) {
                this.completeDeviceSetup();
            }
        });

        // Fired when the caller cancels the connection before its answered by the operator
        device.on('cancel', (connection: Twilio.Connection) => {
            this.deviceStatusSubject.next({
                status: VoiceDeviceStatus.Ready,
            });
        });

        // Fired when the operator connects to an incoming call
        device.on('connect', (connection: Twilio.Connection) => {
            const ticket: Ticket = JSON.parse(connection.customParameters.get('ticket'));
            this.deviceStatusSubject.next({
                status: VoiceDeviceStatus.Busy,
                ticketId: ticket.id,
            });
        });

        // Fired when the operator disconnects the call
        device.on('disconnect', (connection: Twilio.Connection) => {
            this.deviceStatusSubject.next({
                status: VoiceDeviceStatus.Ready,
            });
        });

        // Fired when the operator disconnects the call
        device.on('error', (e) => {
            const ticket: Ticket = JSON.parse(e.connection?.customParameters?.get('ticket'));
            console.error(`Error on device connection with ticketId ${ticket?.id}`);
            console.error(`Message ${e.message}`);
            this.connectionErrorSubject.next({ ticket });
        });

        this.completeDeviceSetup();

        return new Promise((resolve, _) => {
            resolve(new TwilioDevice(device));
        });
    }

    private deviceTokenExpired(): boolean {
        return !!this.expiration && this.expiration <= new Date().getTime() / 1000;
    }

    hasValidToken(): boolean {
        return this.token && !this.deviceTokenExpired();
    }

    shutDownDevice(): void {
        if (this.device) {
            this.device.terminate();
        }
    }

    private completeDeviceSetup(): void {
        const nowTimestamp = new Date().getTime() / 1000;

        firebase
            .firestore()
            .app.auth()
            .currentUser.getIdToken()
            .then((idToken: string) => {
                const headers = new HttpHeaders({
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + idToken,
                });

                const userId = this.authentication.user.id;
                const options = { headers };

                this.http
                    .post(environment.twilio.TOKEN_URL, { clientId: `${userId}` }, options)
                    .pipe(
                        catchError((error) => {
                            return of({});
                        })
                    )
                    .subscribe(
                        (response: TokenResponse) => {
                            if (response && response.token) {
                                this.token = response.token;

                                try {
                                    this.expiration = JSON.parse(atob(this.token.split('.')[1])).exp;
                                } catch (ex) {
                                    this.expiration = nowTimestamp;
                                }

                                const deviceOptions: any = {
                                    codecPreferences: ['opus', 'pcmu'],
                                    fakeLocalDTMF: true,
                                    enableIceRestart: true,
                                    rtcConfiguration: {
                                        iceTransportPolicy: 'relay',
                                        iceServers: response.iceServers,
                                    },
                                };

                                this.device.setup(this.token, deviceOptions);
                            }
                        },
                        (error) => {
                            console.log(error);
                        }
                    );
            });
    }
}

export class TwilioDevice implements VoiceDevice {
    constructor(private twilioDevice: Twilio.Device) {}

    status(): VoiceDeviceStatus {
        switch (this.twilioDevice.status()) {
            case Twilio.Device.Status.Ready:
                return VoiceDeviceStatus.Ready;
            case Twilio.Device.Status.Offline:
                return VoiceDeviceStatus.Offline;
            case Twilio.Device.Status.Busy:
                return VoiceDeviceStatus.Busy;
        }
    }

    async connect(params?: any): Promise<VoiceConnection> {
        return new TwilioConnection(this.twilioDevice.connect(params));
    }

    setup(token: string, options?: any): any {
        this.twilioDevice.setup(token, options);
    }

    terminate(): void {
        this.twilioDevice.destroy();
    }
}

export class TwilioConnection implements VoiceConnection {
    constructor(private twilioConnection: Twilio.Connection) {}

    status(): VoiceConnectionStatus {
        switch (this.twilioConnection.status()) {
            case Twilio.Connection.State.Closed:
                return VoiceConnectionStatus.Closed;
            case Twilio.Connection.State.Connecting:
                return VoiceConnectionStatus.Connecting;
            case Twilio.Connection.State.Open:
                return VoiceConnectionStatus.Open;
            case Twilio.Connection.State.Pending:
                return VoiceConnectionStatus.Pending;
            case Twilio.Connection.State.Reconnecting:
                return VoiceConnectionStatus.Reconnecting;
            case Twilio.Connection.State.Ringing:
                return VoiceConnectionStatus.Ringing;
        }
    }
    isMuted(): boolean {
        return this.twilioConnection.isMuted();
    }
    disconnect(): void {
        this.twilioConnection.disconnect();
    }
    mute(mute: boolean): void {
        this.twilioConnection.mute(mute);
    }
}
